package uz.ferro.erp.feature.customer.ui

import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import mui.material.*
import mui.system.sx
import react.*
import react.dom.events.ChangeEvent
import react.dom.onChange
import react.router.useOutletContext
import uz.ferro.erp.feature.customer.data.CustomerRepository
import uz.ferro.erp.feature.customer.data.SalesRepository
import uz.ferro.erp.feature.customer.model.CustomerAddressDto
import uz.ferro.erp.feature.customer.model.CustomerDto
import uz.ferro.erp.feature.customer.model.SalesPointDto
import uz.ferro.erp.feature.home.ui.DashboardUI
import uz.ferro.erp.ui.HorizontalLayout
import uz.ferro.erp.ui.VerticalLayout
import uz.ferro.erp.ui.table.CellItem
import uz.ferro.erp.ui.table.HeaderItem
import uz.ferro.erp.ui.table.RowItem
import uz.ferro.erp.ui.table.TableView
import web.cssom.AlignItems
import web.cssom.Auto
import web.cssom.Position
import web.cssom.number
import web.cssom.pct
import web.cssom.px
import web.html.HTMLInputElement

val CustomerListPage = FC {
    val dashboardUI = useOutletContext<DashboardUI>()

    // filter params
    var searchQuery by useState("")
    var salesPoint: SalesPointDto? by useState()
    var county: String? by useState()

    // data
    var customers by useState<List<CustomerDto>>(emptyList())
    var salesPoints by useState<List<SalesPointDto>>(emptyList())
    var filteredCustomers by useState<List<CustomerDto>>(emptyList())
    var countyList: List<String?> by useState(emptyList())

    var countyByGroup by useState<Map<Long, List<String>>>(emptyMap())

    fun CustomerDto.primaryAddress(): CustomerAddressDto? {
        val addressShips = this.addresses?.filter { it.type == "SHIPPING" }
        val addressBills = this.addresses?.filter { it.type == "BILLING" }
        val addressShipWithLocation =
            addressShips?.firstOrNull { it.latitude != null && it.longitude != null }
        val addressBillWithLocation =
            addressBills?.firstOrNull { it.latitude != null && it.longitude != null }

        val address = addressShipWithLocation ?: addressBillWithLocation ?: addressShips?.firstOrNull()
        ?: addressBills?.firstOrNull()
        return address
    }

    fun runFilter() {
        val filteredData = mutableListOf<CustomerDto>()

        for (customer in customers) {
            if (customer.name?.contains(searchQuery.trim(), ignoreCase = true) == true &&
                (salesPoint == null || customer.groupId?.equals(salesPoint?.customerGroupId) == true) &&
                (county == null || customer.primaryAddress()?.county?.equals(county) == true)
            ) {
                filteredData.add(customer)
                if (filteredData.size >= 1000) break
            }
        }

        filteredCustomers = filteredData
    }

    fun loadData() {
        MainScope().launch {
            try {
                val customerData = CustomerRepository.getCustomers()
                val salesPointData = SalesRepository.getSalesPoints()
                val countyMap = mutableMapOf<Long, MutableSet<String>>()
                customerData.forEach { c ->
                    val groupId = c.groupId ?: 0
                    val county = c.primaryAddress()?.county.orEmpty()
                    if (groupId > 0 && county.isNotBlank()) {
                        countyMap.getOrPut(groupId) {
                            mutableSetOf()
                        }.add(county)
                    }
                }

                countyByGroup = countyMap.mapValues { it.value.toList().sortedBy { s -> s.lowercase() } }
                customers = customerData
                salesPoints = salesPointData
            } catch (e: Exception) {
                console.error(e)
            }
        }
    }

    useEffectOnce {
        dashboardUI.setTitle("Mijozlar")
        loadData()
    }

    useEffect(searchQuery, salesPoint, county, customers) {
        runFilter()
    }

    useEffect(salesPoint, countyByGroup) {
        countyList = if (salesPoint == null) {
            countyByGroup.values.flatten().toSet().sortedBy { s -> s.lowercase() }
        } else {
            countyByGroup[salesPoint?.customerGroupId].orEmpty().sortedBy { s -> s.lowercase() }
        }
    }

    VerticalLayout {
        space = 16.px
        sx {
            padding = 16.px
            alignItems = AlignItems.stretch
            flexGrow = number(1.0)
        }

        HorizontalLayout {
            space = 16.px

            TextField {
                placeholder = "Qidiruv"
                size = Size.small
                value = searchQuery
                onChange = { event ->
                    event as ChangeEvent<HTMLInputElement>
                    searchQuery = event.target.value
                }
                sx {
                    paddingTop = 4.px
                    paddingBottom = 4.px
                    width = 250.px
                    height = 40.px
                }
            }

            FormControl {
                sx {
                    paddingTop = 4.px
                    paddingBottom = 4.px
                }
                InputLabel {
                    +"Sotuv joyi"
                    shrink = true
                }
                Select {
                    sx {
                        width = 200.px
                    }

                    displayEmpty = true
                    value = salesPoint?.id.orEmpty()
                    label = ReactNode("Sotuv joyi")
                    size = Size.small

                    onChange = { event, _ ->
                        salesPoint = salesPoints.find { sPoint ->
                            sPoint.id == event.target.value
                        }
                        county = null
                    }

                    salesPoints.forEach { sp ->
                        MenuItem {
                            value = sp.id
                            +sp.id
                        }
                    }
                }
            }

            FormControl {
                sx {
                    paddingTop = 4.px
                    paddingBottom = 4.px
                }
                InputLabel {
                    +"Hududlar"
                    shrink = true
                }
                Select {
                    sx {
                        width = 200.px
                    }

                    displayEmpty = true
                    value = county.orEmpty()
                    label = ReactNode("Hududlar")
                    size = Size.small

                    onChange = { event, _ -> county = event.target.value }

                    countyList.forEach { cont ->
                        MenuItem {
                            value = cont
                            +cont
                        }
                    }
                }
            }

        }

        Paper {
            sx {
                position = Position.relative
                width = 100.pct
                flexGrow = number(1.0)
                overflowY = Auto.auto
            }

            TableView {
                headers = listOf(
                    HeaderItem("ID"),
                    HeaderItem("Nomi"),
                    HeaderItem("Balansi"),
                    HeaderItem("Hudud")
                )
                items = filteredCustomers
                    .mapIndexed { index, customer ->
                        RowItem(
                            key = index.toString(),
                            cells = listOf(
                                CellItem(
                                    title = customer.id
                                ),
                                CellItem(
                                    title = customer.name + " $index"
                                ),
                                CellItem(
                                    title = customer.balance?.asDynamic().toFixed(2)
                                ),
                                CellItem(
                                    title = customer.primaryAddress()?.county
                                )
                            )
                        )
                    }
            }
        }
    }
}